import { collection, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { daysBetween, medianDays, scorePercent } from '@/lib/format';
import type { Candidate, Match, Nomination, Opportunity, Org, Recommender } from '@/types/domain';
import { strings } from '@/strings';

const THIRTY_DAYS_MS = 30 * 86_400_000;
const NINETY_DAYS_MS = 90 * 86_400_000;

/**
 * Only engagements old enough to have reached the window can answer it;
 * including younger ones would flatter the number. That is why each window
 * filters its own eligible set rather than sharing one, and why a window with
 * nothing eligible returns null instead of 0 — no verdict is not a bad verdict.
 */
function survival(matches: Array<Match & { id: string }>, now: number, windowMs: number): number | null {
  const eligible = matches.filter((m) => m.activatedAt && now - m.activatedAt >= windowMs);
  if (eligible.length === 0) return null;
  const survived = eligible.filter((m) => m.status === 'active' || m.status === 'completed');
  return survived.length / eligible.length;
}

/**
 * The funnel, counted from the collections rather than from a maintained
 * counter. At this size that is both simpler and more truthful: a denormalized
 * count drifts, and a drifted number in a report is worse than a slow one.
 *
 * Durations are medians, not means — one candidate who sat in the queue for a
 * year would drag an average past anything a real person experiences.
 */
export function ReportsPage() {
  const { rows: nominations } = useDocs<Nomination>(query(collection(db, 'nominations'), orderBy('createdAt', 'desc')), []);
  const { rows: candidates } = useDocs<Candidate>(query(collection(db, 'candidates'), orderBy('createdAt', 'desc')), []);
  const { rows: orgs } = useDocs<Org>(query(collection(db, 'orgs'), orderBy('createdAt', 'desc')), []);
  const { rows: opportunities } = useDocs<Opportunity>(query(collection(db, 'opportunities'), orderBy('createdAt', 'desc')), []);
  const { rows: matches } = useDocs<Match>(query(collection(db, 'matches'), orderBy('createdAt', 'desc')), []);
  const { rows: recommenders } = useDocs<Recommender>(query(collection(db, 'recommenders'), orderBy('updatedAt', 'desc')), []);

  const now = Date.now();

  const toVetted = candidates
    .map((c) => daysBetween(c.createdAt, (c as { vettedAt?: number }).vettedAt))
    .filter((d): d is number => d !== null);

  // Published, not created: the clock a organization cares about starts when
  // the card went live, and reviewedAt is when reviewOpportunity published it.
  const toActivated = matches
    .map((m) => {
      const card = opportunities.find((o) => o.id === m.opportunityId) as { reviewedAt?: number } | undefined;
      return daysBetween(card?.reviewedAt, m.activatedAt);
    })
    .filter((d): d is number => d !== null);

  const medianVetted = medianDays(toVetted);
  const medianActivated = medianDays(toActivated);

  const tiles = [
    { id: 'nominations', label: strings.reportNominations, value: String(nominations.length) },
    { id: 'candidates', label: strings.reportCandidates, value: String(candidates.length) },
    { id: 'vetted', label: strings.reportVetted, value: String(candidates.filter((c) => c.stage === 'vetted').length) },
    { id: 'orgs', label: strings.reportOrgs, value: String(orgs.filter((o) => o.status === 'approved').length) },
    { id: 'assessments', label: strings.reportAssessments,
      value: String(orgs.filter((o) => (o as { assessmentAt?: number }).assessmentAt).length) },
    { id: 'opportunities-live', label: strings.reportOpportunitiesLive, value: String(opportunities.filter((o) => o.status === 'live').length) },
    { id: 'matches-active', label: strings.reportMatchesActive, value: String(matches.filter((m) => m.status === 'active').length) },
    { id: 'time-to-vetted', label: strings.reportTimeToVetted,
      value: medianVetted === null ? '—' : `${medianVetted} ${strings.unitDays}` },
    { id: 'time-to-activated', label: strings.reportTimeToActivated,
      value: medianActivated === null ? '—' : `${medianActivated} ${strings.unitDays}` },
  ];

  const survivalTiles = [
    { id: 'survival-30', label: strings.reportSurvival30, value: survival(matches, now, THIRTY_DAYS_MS) },
    { id: 'survival-90', label: strings.reportSurvival90, value: survival(matches, now, NINETY_DAYS_MS) },
  ].filter((t) => t.value !== null);

  const empty = nominations.length === 0 && candidates.length === 0 && orgs.length === 0
    && opportunities.length === 0 && matches.length === 0;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.reportsTitle}</h1>
      {empty ? <p className="text-muted">{strings.reportNoData}</p> : null}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {tiles.map((tile) => (
          <div
            key={tile.id}
            data-testid={`tile-${tile.id}`}
            className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-white p-4"
          >
            <span className="text-sm text-muted">{tile.label}</span>
            <span className="text-2xl font-bold text-primary">{tile.value}</span>
          </div>
        ))}
        {survivalTiles.map((tile) => (
          <div
            key={tile.id}
            data-testid={`tile-${tile.id}`}
            className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-white p-4"
          >
            <span className="text-sm text-muted">{tile.label}</span>
            <span className="text-2xl font-bold text-primary">{scorePercent(tile.value as number)}</span>
          </div>
        ))}
      </div>

      {recommenders.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-ink">{strings.reportRecommendersTitle}</h2>
          <div className="hidden grid-cols-[2fr,1fr,1fr,1fr] gap-3 px-4 text-sm font-medium text-muted md:grid">
            <span>{strings.reportColRecommender}</span>
            <span>{strings.reportColSubmitted}</span>
            <span>{strings.reportColVetted}</span>
            <span>{strings.reportColQuality}</span>
          </div>
          <ul className="flex flex-col gap-2">
            {recommenders.map((r) => (
              <li
                key={r.id}
                data-testid={`recommender-${r.id}`}
                className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4 md:grid md:grid-cols-[2fr,1fr,1fr,1fr] md:items-center md:gap-3 md:py-3"
              >
                <span className="font-semibold text-ink md:font-normal">{r.name}</span>
                <span className="text-sm text-muted" dir="ltr">{r.stats?.submitted ?? 0}</span>
                <span className="text-sm text-muted" dir="ltr">{r.stats?.vetted ?? 0}</span>
                {/* Null is not zero: below the minimum sample the score is not
                    computed at all, and showing 0٪ would libel a new
                    recommender who has made two good nominations. */}
                <span className="text-sm text-muted">
                  {r.stats?.qualityScore === null || r.stats?.qualityScore === undefined
                    ? strings.reportNotEnoughYet
                    : scorePercent(r.stats.qualityScore)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
